import { useState } from 'react';

import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { Dialog } from '@/components/Dialog/Dialog';
import { Select } from '@/components/Select/Select';
import { TextField } from '@/components/TextField/TextField';
import type { BrokerageSummary } from '@/graphql/brokerages';
import { PORTFOLIO_ACCESS_NAMES, type PortfolioAccess } from '@/lib/brokerage-permissions';
import { brokerageName } from '@/lib/brokerages';

import styles from './PortfolioForm.module.css';

export interface PortfolioChange {
  tenantId: string;
  access: Exclude<PortfolioAccess, 'CUSTOM'>;
}

export interface PortfolioFormProps {
  /** Who is being changed, as the page names them. */
  person: string;
  brokerages: readonly BrokerageSummary[];
  /** What they hold now, per brokerage id; absent means not managed. */
  current: Readonly<Record<string, PortfolioAccess>>;
  busy?: boolean;
  error?: string | null;
  onSubmit: (changes: PortfolioChange[]) => void;
  onCancel: () => void;
}

const SEARCH_FROM = 8;

/** Choose which brokerages a staff member manages, and how much they may change there. */
export function PortfolioForm({
  person,
  brokerages,
  current,
  busy = false,
  error = null,
  onSubmit,
  onCancel,
}: PortfolioFormProps) {
  const [draft, setDraft] = useState<Record<string, PortfolioAccess>>({ ...current });
  const [search, setSearch] = useState('');

  const accessOf = (id: string) => draft[id] ?? 'NONE';
  const changes: PortfolioChange[] = brokerages
    .filter((b) => accessOf(b.id) !== (current[b.id] ?? 'NONE') && accessOf(b.id) !== 'CUSTOM')
    .map((b) => ({ tenantId: b.id, access: accessOf(b.id) as PortfolioChange['access'] }));

  const needle = search.trim().toLowerCase();
  const shown = [...brokerages]
    .sort((a, b) => {
      const managed =
        Number((current[b.id] ?? 'NONE') !== 'NONE') - Number((current[a.id] ?? 'NONE') !== 'NONE');
      return managed || brokerageName(a).localeCompare(brokerageName(b));
    })
    .filter((b) => !needle || brokerageName(b).toLowerCase().includes(needle));

  const optionsFor = (id: string): (readonly [string, string])[] => {
    const options: PortfolioAccess[] =
      current[id] === 'CUSTOM' ? ['NONE', 'VIEW', 'MANAGE', 'CUSTOM'] : ['NONE', 'VIEW', 'MANAGE'];
    return options.map((access) => [access, PORTFOLIO_ACCESS_NAMES[access]] as const);
  };

  return (
    <Dialog
      actions={
        <>
          <Button disabled={busy} onClick={onCancel} variant="ghost">
            Cancel
          </Button>
          <Button disabled={changes.length === 0} loading={busy} onClick={() => onSubmit(changes)}>
            Save brokerages
          </Button>
        </>
      }
      description="Managing a brokerage does not make them a member of it. “Manage everything” lets them change its members, roles, branding and setup."
      dismissable={!busy}
      onClose={onCancel}
      placement="drawer"
      title={`Brokerages for ${person}`}
    >
      <div className={styles.form}>
        {brokerages.length >= SEARCH_FROM ? (
          <TextField
            label="Find a brokerage"
            onChange={(e) => setSearch(e.target.value)}
            value={search}
          />
        ) : null}
        {brokerages.length === 0 ? (
          <p className={styles.empty}>There are no brokerages yet.</p>
        ) : (
          <ul aria-label="Brokerages" className={styles.list}>
            {shown.map((brokerage) => (
              <li className={styles.row} key={brokerage.id}>
                <Select
                  disabled={busy}
                  label={brokerageName(brokerage)}
                  onChange={(value) =>
                    setDraft((d) => ({ ...d, [brokerage.id]: value as PortfolioAccess }))
                  }
                  options={optionsFor(brokerage.id)}
                  value={accessOf(brokerage.id)}
                />
              </li>
            ))}
          </ul>
        )}
        {error ? (
          <Callout tone="danger" title="That didn't work">
            {error}
          </Callout>
        ) : null}
      </div>
    </Dialog>
  );
}
